import React, { useEffect, useState } from "react";
import { getFormContentPreview } from "../../../../api/services/services";
import { useLocation } from "react-router-dom";
import { Button, Typography, Table, Box, CircularProgress, Paper, TableContainer, TableHead, TableBody, TableCell, TableRow, Grid } from "@mui/material";
import moment from "moment";
import PremiumPublicLayout from "../../../templates/PremiumPublicLayout";
import PremiumCard from "../../../molecules/PremiumCard";
import SectionHeader from "../../../molecules/SectionHeader";
import PackingMaterialDownload from "./download";
import PrintComponent from "../../PrintComponent";

import DescriptionIcon from "@mui/icons-material/Description";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";

const PackingMaterialIssuedClient = ({ formContent: propsFormContent, surveyData: propsSurveyData }) => {
  const [formContent, setFormContent] = useState(propsFormContent || null);
  const [surveyData, setSurveyData] = useState(propsSurveyData || []);
  const [isLoading, setIsLoading] = useState(!propsFormContent);
  const [isValid, setIsValid] = useState(!!propsFormContent);

  const location = useLocation();
  const urlData = new URLSearchParams(location.search);
  const email = urlData.get("email");
  const surveyId = urlData.get("surveyId");
  const formId = urlData.get("formId");
  const token = urlData.get("token");

  const getContent = () => {
    if (propsFormContent) return;
    setIsLoading(true);
    getFormContentPreview(surveyId, formId, email, token)
      .then((res) => {
        setFormContent(res.data.data.content);
        setIsValid(true);
      })
      .catch(() => {
        setIsValid(false);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!propsFormContent) {
      getContent();
    } else {
      setFormContent(propsFormContent);
      setSurveyData(propsSurveyData);
      setIsValid(true);
      setIsLoading(false);
    }
  }, [propsFormContent, propsSurveyData]);

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <CircularProgress />
    </Box>
  );

  const pageActions = (
    <Button
      variant="contained"
       onClick={() => {
        if (window.innerWidth <= 768) { alert("For a good experience, please use a desktop screen"); return; }
                                const tempTitle = document.title;
        const newTitle = `Packing-Material-${formContent?.customerName || 'Document'}-${moment().format("DD.MM.YYYY")}`;
        document.title = newTitle;
        
        // Standardized delay for print and title restoration
        setTimeout(() => {
          window.print();
          setTimeout(() => {
            document.title = tempTitle;
          }, 2000);
        }, 500);
      }}
      sx={{ 
        textTransform: "none", 
        borderRadius: "var(--radius-md)", 
        padding: "10px 24px",
        fontWeight: 700,
        backgroundColor: "var(--text-main)",
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.2)",
        '&:hover': { backgroundColor: '#000' }
      }}
      startIcon={<DescriptionIcon />}
    >
      Save as PDF
    </Button>
  );

  return (
    <>
      <PremiumPublicLayout title="Packing Material Issued" actions={isValid ? pageActions : null} hideBackButton={true}>
        {!isValid ? (
          <Box sx={{ p: 10, textAlign: 'center', backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #fee2e2' }}>
            <Typography variant="h5" color="error" sx={{ fontWeight: 700 }}>Invalid link or data not available.</Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>Please contact the administrator if you believe this is an error.</Typography>
          </Box>
        ) : (
          <>
            <div className="no-print">
              <SectionHeader title="Customer Details" icon={<PersonIcon />} />
              <PremiumCard sx={{ mb: 4, p: 3 }}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer Name</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, color: '#1a202c' }}>{formContent?.customerName || "-"}</Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Packing/Shifting Date Wise</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, color: '#1a202c' }}>{formContent?.packingShifting || "-"}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Vehicles Used</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, color: '#1a202c' }}>{formContent?.vehicles || "-"}</Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Packers Used</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5, color: '#1a202c' }}>{formContent?.packers || "-"}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </PremiumCard>

              <SectionHeader title="Material Inventory" icon={<InventoryIcon />} />
              <PremiumCard sx={{ mb: 4, overflow: 'hidden', borderRadius: '12px' }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700, py: 2, borderBottom: '2px solid #e2e8f0' }}>No.</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, borderBottom: '2px solid #e2e8f0' }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, borderBottom: '2px solid #e2e8f0' }}>Date Issued</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, borderBottom: '2px solid #e2e8f0' }}>Qty Issued</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, borderBottom: '2px solid #e2e8f0' }}>Qty Used</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 2, borderBottom: '2px solid #e2e8f0' }}>Qty Returned</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formContent?.dataRows?.map((row, idx) => (
                        <TableRow key={idx} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ py: 1.5 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ py: 1.5, fontWeight: 600 }}>{row.description || "-"}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>{row.date1 ? moment(row.date1).format("Do MMM, YYYY") : "-"}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>{row.qtyIssued || "-"}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--primary-color)' }}>
                              {Number(row.qtyIssued || 0) - Number(row.qtyReturned || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>{row.qtyReturned || "-"}</TableCell>
                        </TableRow>
                      ))}
                      {(!formContent?.dataRows || formContent.dataRows.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                            <Typography variant="body2" color="textSecondary">No items recorded</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </PremiumCard>
            </div>
          </>
        )}
      </PremiumPublicLayout>
      <PrintComponent>
        <PackingMaterialDownload formContent={formContent} />
      </PrintComponent>
    </>
  );
};

export default PackingMaterialIssuedClient;
